import { useEffect, useState } from 'react'

import useCurrencyState from 'app/hooks/useCurrencyState'
import type { AddressObject } from 'app/services/Geosuggest/Geosuggest.types'
import { useGetEstimateRateMutation } from 'app/services/Query/shipping/shipping.mutation'
import useAuthStore, { getAuthUser, getIsConfirmed } from 'app/store/auth.store'

import useUserAddress from '../../../hooks/useUserAddress'

interface UseShippingCost {
  slug: string
  enabled?: boolean
}

const useShippingCost = ({ slug, enabled = true }: UseShippingCost) => {
  const deviceAddress = useUserAddress()
  const { currency } = useCurrencyState()

  const [cost, setCost] = useState<EstimateRateModel>({
    estimate_rate: null,
    converted_estimate_rate: null,
    estimate_rate_currency: null
  })
  const user = useAuthStore(getAuthUser)
  const isAuth = useAuthStore(getIsConfirmed)

  const isSeveralAddresses = !!user.addresses && user.addresses.length > 1
  const isNoAddress = !user.addresses || !user.addresses.length

  const { mutateAsync } = useGetEstimateRateMutation()

  const calculate = async (
    address?: (Partial<AddressObject> & Partial<ShippingAddresses>) | null
  ) => {
    const hasAddress =
      !!address?.id ||
      !!address?.country ||
      !!address?.zip_code ||
      !!address?.postal_code

    const values = {
      listing_slug: slug,
      address_id: address?.id ?? '',
      country: address?.country ?? '',
      zip_code: (address?.zip_code || address?.postal_code) ?? ''
    }

    if (enabled && hasAddress) {
      const res = await mutateAsync(values)
      setCost(res)
    }
  }

  useEffect(() => {
    if (!slug) return

    if (!!user.addresses && user.addresses.length) {
      calculate(user.addresses[0])
    } else {
      calculate(deviceAddress)
    }
  }, [deviceAddress?.country, currency, isAuth, slug])

  return {
    shippingCost: cost,
    isSeveralAddresses,
    isNoAddress,
    addresses: user?.addresses || [],
    calculateShippingCost: calculate,
    isDeviceLocation: !!deviceAddress
  }
}

export default useShippingCost
